import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, KeyRound, Plus, ShieldAlert, X } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const statusStyles = {
  active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  suspended: 'border-amber-300 bg-amber-50 text-amber-800',
  revoked: 'border-red-300 bg-red-50 text-red-700',
};

const assurancePolicyStyles = {
  internal_authentication_ready: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  internal_evidence_ready: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  internal_foundation: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  not_ready: 'border-amber-300 bg-amber-50 text-amber-800',
  blocked: 'border-red-300 bg-red-50 text-red-700',
};

const actionLabels = {
  'identity.created_from_verification': 'Created from verification',
  'identity.viewed': 'Viewed',
  'identity.suspended': 'Suspended',
  'identity.reactivated': 'Reactivated',
  'identity.revoked': 'Revoked',
  'identity.device.created': 'Test device added',
  'identity.device.blocked': 'Device blocked',
  'identity.device.removed': 'Device removed',
  'identity.session.revoked': 'Session revoked',
  'identity.authentication.requested': 'Authentication requested',
  'identity.authentication.approved': 'Authentication approved',
  'identity.authentication.denied': 'Authentication denied',
  'identity.authentication.expired': 'Authentication expired',
  'identity.session.created': 'Identity session created',
  'identity.consent.created': 'Consent created',
  'identity.consent.granted': 'Consent granted',
  'identity.consent.revoked': 'Consent revoked',
  'identity.consent.expired': 'Consent expired',
  'identity.claim_sharing.prepared': 'Claim sharing prepared',
  'identity.claim_sharing.completed': 'Claim sharing completed',
  'identity.claim_sharing.cancelled': 'Claim sharing cancelled',
  'identity.claim_sharing.blocked': 'Claim sharing blocked',
  'digital_key.account.created': 'Digital key account created',
  'digital_key.account.eligibility_evaluated': 'Digital key eligibility evaluated',
  'digital_key.account.activated': 'Digital key account activated',
  'digital_key.account.suspended': 'Digital key account suspended',
  'digital_key.account.reactivated': 'Digital key account reactivated',
  'digital_key.account.revoked': 'Digital key account revoked',
  'digital_key.login_request.created': 'Digital key login request created',
  'digital_key.login_request.approved': 'Digital key login request approved',
  'digital_key.login_request.denied': 'Digital key login request denied',
  'digital_key.login_request.expired': 'Digital key login request expired',
  'digital_key.login_request.cancelled': 'Digital key login request cancelled',
  'digital_key.recovery_case.created': 'Recovery case opened',
  'digital_key.recovery_case.in_review': 'Recovery case marked in review',
  'digital_key.recovery_case.resolved': 'Recovery case resolved',
  'digital_key.recovery_case.rejected': 'Recovery case rejected',
  'digital_key.recovery_case.cancelled': 'Recovery case cancelled',
  'digital_key.recovery_case.emergency_hold_applied': 'Emergency hold applied',
  'oidc_lab.authorization.started': 'OIDC lab authorization started',
  'oidc_lab.authorization.finalized': 'OIDC lab authorization finalized',
  'oidc_lab.code.issued': 'OIDC lab code issued',
  'oidc_lab.token.exchanged': 'OIDC lab token exchanged',
  'oidc_lab.userinfo.viewed': 'OIDC lab userinfo viewed',
};

const digitalKeyLoginClaimKeys = new Set([
  'identity_verified', 'full_name_verified', 'email_verified',
  'age_over_16', 'age_over_18', 'age_over_21', 'document_verified',
  'selfie_match_verified', 'identity_active', 'trusted_device_available',
  'authentication_capable', 'digital_key_account_active', 'device_bound',
  'internal_assurance_policy',
]);

const recoveryCaseTypes = [
  ['lost_device', 'Lost device'],
  ['suspected_compromise', 'Suspected compromise'],
  ['account_recovery', 'Account recovery'],
  ['device_replacement', 'Device replacement'],
  ['manual_review', 'Manual review'],
  ['suspicious_login_request', 'Suspicious login request'],
];

const recoveryActionLabels = [
  ['suspend_digital_key_account', 'Suspend digital key account'],
  ['review_trusted_devices', 'Review trusted devices'],
  ['review_identity_sessions', 'Review identity sessions'],
  ['block_suspicious_device', 'Block suspicious device'],
  ['revoke_active_sessions', 'Revoke active sessions'],
  ['manual_identity_review', 'Manual identity review'],
  ['prepare_device_replacement', 'Prepare device replacement'],
  ['prepare_account_recovery', 'Prepare account recovery'],
];

const identityTabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'assurance_claims', label: 'Assurance & Claims' },
  { id: 'devices_sessions', label: 'Devices & Sessions' },
  { id: 'consent_sharing', label: 'Consent & Sharing' },
  { id: 'audit', label: 'Audit' },
];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function approvalSummaryLabel(context) {
  if (!context) return 'Admin override';
  if (context.approval_mode === 'consumer_acknowledged') return 'Consumer acknowledgement';
  if (context.device_bound) return 'Device-bound';
  return context.policy_override ? 'Admin override (policy override)' : 'Admin override';
}

function IdentityStatus({ status }) {
  const label = String(status || 'active').replaceAll('_', ' ');
  return (
    <span className={`inline-flex items-center gap-1.5 border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[status] || statusStyles.active}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>
      {label}
    </span>
  );
}

function DigitalKeyAssurancePolicyStatus({ status }) {
  const label = String(status || 'not_ready').replaceAll('_', ' ');
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${assurancePolicyStyles[status] || assurancePolicyStyles.not_ready}`}>{label}</span>;
}

function DigitalKeyStatus({ status }) {
  const tone = {
    draft: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    suspended: 'border-amber-300 bg-amber-50 text-amber-800',
    revoked: 'border-red-300 bg-red-50 text-red-700',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'draft').replaceAll('_', ' ')}</span>;
}

function DigitalKeyEligibilityStatus({ status }) {
  const tone = {
    eligible: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    not_eligible: 'border-red-300 bg-red-50 text-red-700',
    needs_review: 'border-amber-300 bg-amber-50 text-amber-800',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  const label = {
    eligible: 'Eligible',
    not_eligible: 'Not eligible',
    needs_review: 'Needs review',
  }[status] || String(status || 'needs_review').replaceAll('_', ' ');
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{label}</span>;
}

function DigitalKeyLoginRequestStatus({ status }) {
  const tone = {
    pending: 'border-amber-300 bg-amber-50 text-amber-800',
    approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    denied: 'border-red-300 bg-red-50 text-red-700',
    expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'pending').replaceAll('_', ' ')}</span>;
}

function DigitalKeyDeviceStatus({ status }) {
  const tone = {
    pending: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    blocked: 'border-amber-300 bg-amber-50 text-amber-800',
    removed: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'pending').replaceAll('_', ' ')}</span>;
}

function RecoveryCaseStatus({ status }) {
  const tone = {
    open: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    in_review: 'border-amber-300 bg-amber-50 text-amber-800',
    resolved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    rejected: 'border-red-300 bg-red-50 text-red-700',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'open').replaceAll('_', ' ')}</span>;
}

function RecoveryCaseSeverity({ severity }) {
  const tone = {
    low: 'border-zinc-200 bg-white text-zinc-600',
    medium: 'border-amber-200 bg-amber-50 text-amber-800',
    high: 'border-orange-200 bg-orange-50 text-orange-800',
    critical: 'border-red-300 bg-red-50 text-red-700',
  }[severity] || 'border-zinc-200 bg-white text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(severity || 'medium')}</span>;
}

function digitalKeyCheckLabel(key) {
  return {
    identity_active: 'Identity active',
    source_verification_available: 'Source verification available',
    identity_verified_claim_available: 'Identity verification signal available',
    document_verified_claim_available: 'Document verification signal available',
    trusted_device_available: 'Trusted device available',
  }[key] || key.replaceAll('_', ' ');
}

function SummaryRow({ label, children }) {
  return (
    <div className="grid grid-cols-[minmax(8rem,0.7fr)_minmax(0,1.8fr)] gap-4 border-b border-zinc-100 py-3 last:border-b-0">
      <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-zinc-800">{children || '—'}</dd>
    </div>
  );
}

function EventMetadata({ metadata }) {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-500">
      {Object.entries(metadata).map(([key, value]) => (
        <span key={key}>{key.replaceAll('_', ' ')}: {Array.isArray(value) ? value.map((item) => typeof item === 'object' ? `${item.claim_key} (${item.reason})` : item).join(', ') : String(value)}</span>
      ))}
    </div>
  );
}

function eventLabel(action) {
  if (actionLabels[action]) return actionLabels[action];
  return String(action || 'Unknown event')
    .replace(/^identity\./, '')
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function DeviceStatus({ status }) {
  const tone = {
    trusted: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    blocked: 'border-amber-300 bg-amber-50 text-amber-800',
    removed: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function SessionStatus({ status }) {
  const tone = {
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    revoked: 'border-red-300 bg-red-50 text-red-700',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function AuthenticationRequestStatus({ status }) {
  const tone = {
    pending: 'border-amber-300 bg-amber-50 text-amber-800',
    approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    denied: 'border-red-300 bg-red-50 text-red-700',
    expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function humanizeAssuranceValue(value) {
  const normalized = String(value || 'unknown');
  return {
    kyc: 'KYC',
    idv: 'IDV',
    manual: 'Manual',
    unknown: 'Unknown',
  }[normalized] || normalized.replaceAll('_', ' ');
}

function AssuranceCandidateLevel({ level }) {
  const tone = {
    incomplete: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    basic_candidate: 'border-amber-300 bg-amber-50 text-amber-800',
    substantial_candidate: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    high_candidate: 'border-emerald-400 bg-emerald-50 text-emerald-800',
  }[level] || 'border-zinc-200 bg-zinc-50 text-zinc-700';
  const label = {
    incomplete: 'Incomplete',
    basic_candidate: 'Foundation',
    substantial_candidate: 'Evidence ready',
    high_candidate: 'Authentication ready',
  }[level] || humanizeAssuranceValue(level);
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{label}</span>;
}

function VerifiedClaimStatus({ status }) {
  const tone = {
    verified: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    unverified: 'border-amber-300 bg-amber-50 text-amber-800',
    not_available: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  const label = {
    verified: 'Verified',
    unverified: 'Unverified',
    not_available: 'Not available',
  }[status] || humanizeAssuranceValue(status);
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{label}</span>;
}

function ConsentStatus({ status }) {
  const tone = {
    draft: 'border-zinc-200 bg-zinc-50 text-zinc-700',
    granted: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    revoked: 'border-red-300 bg-red-50 text-red-700',
    expired: 'border-amber-300 bg-amber-50 text-amber-800',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function ClaimSharingStatus({ status }) {
  const tone = {
    prepared: 'border-amber-300 bg-amber-50 text-amber-800',
    completed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
    blocked: 'border-red-300 bg-red-50 text-red-700',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function claimSafeValue(claim) {
  if (typeof claim.safe_value === 'boolean') return claim.safe_value ? 'Yes' : 'No';
  return claim.safe_value || '—';
}

function IdentityDetailTabs({ activeTab, onChange }) {
  return (
    <div className="mb-6 overflow-x-auto border-b border-zinc-200" role="tablist" aria-label="Identity detail sections">
      <div className="flex min-w-max gap-1">
        {identityTabs.map((tab) => (
          <button key={tab.id} id={`identity-tab-${tab.id}`} type="button" role="tab" aria-selected={activeTab === tab.id} aria-controls={`identity-panel-${tab.id}`} onClick={() => onChange(tab.id)}
            className={`border-b-2 px-3 py-2.5 text-sm transition-colors ${activeTab === tab.id ? 'border-black font-medium text-black' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-800'}`}>
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function truncateUserAgent(value) {
  const userAgent = String(value || '');
  return userAgent.length > 68 ? `${userAgent.slice(0, 65)}...` : userAgent || '—';
}

export default function IdentityDetail() {
  const { id } = useParams();
  const [identity, setIdentity] = useState(null);
  const [devices, setDevices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [authenticationRequests, setAuthenticationRequests] = useState([]);
  const [assuranceProfile, setAssuranceProfile] = useState(null);
  const [verifiedClaims, setVerifiedClaims] = useState(null);
  const [consents, setConsents] = useState([]);
  const [relyingPartyProfiles, setRelyingPartyProfiles] = useState([]);
  const [claimSharingTransactions, setClaimSharingTransactions] = useState([]);
  const [digitalKeyAccount, setDigitalKeyAccount] = useState(null);
  const [digitalKeyAssurancePolicy, setDigitalKeyAssurancePolicy] = useState(null);
  const [digitalKeyLoginRequests, setDigitalKeyLoginRequests] = useState([]);
  const [digitalKeyRecoveryCases, setDigitalKeyRecoveryCases] = useState([]);
  const [digitalKeyDevices, setDigitalKeyDevices] = useState([]);
  const [digitalKeyActivationLink, setDigitalKeyActivationLink] = useState('');
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditPagination, setAuditPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [auditFilters, setAuditFilters] = useState({ type: '', from: '', to: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditEvent, setSelectedAuditEvent] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [testDeviceOpen, setTestDeviceOpen] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState('');
  const [deviceType, setDeviceType] = useState('unknown');
  const [pendingAction, setPendingAction] = useState(null);
  const [authenticationStartOpen, setAuthenticationStartOpen] = useState(false);
  const [authenticationDeviceId, setAuthenticationDeviceId] = useState('');
  const [authenticationPurpose, setAuthenticationPurpose] = useState('internal_test');
  const [pendingAuthenticationAction, setPendingAuthenticationAction] = useState(null);
  const [consentDraftOpen, setConsentDraftOpen] = useState(false);
  const [consentRelyingPartyProfileId, setConsentRelyingPartyProfileId] = useState('');
  const [consentRelyingPartyName, setConsentRelyingPartyName] = useState('');
  const [consentPurpose, setConsentPurpose] = useState('');
  const [consentRequestedClaims, setConsentRequestedClaims] = useState([]);
  const [consentExpiresAt, setConsentExpiresAt] = useState('');
  const [pendingConsentAction, setPendingConsentAction] = useState(null);
  const [consentReason, setConsentReason] = useState('');
  const [claimSharingConsentId, setClaimSharingConsentId] = useState('');
  const [pendingClaimSharingCancellation, setPendingClaimSharingCancellation] = useState(null);
  const [claimSharingCancellationReason, setClaimSharingCancellationReason] = useState('');
  const [pendingDigitalKeyAction, setPendingDigitalKeyAction] = useState(null);
  const [digitalKeyReason, setDigitalKeyReason] = useState('');
  const [digitalKeyLoginFormOpen, setDigitalKeyLoginFormOpen] = useState(false);
  const [digitalKeyLoginAssurance, setDigitalKeyLoginAssurance] = useState('internal_foundation');
  const [digitalKeyLoginClaims, setDigitalKeyLoginClaims] = useState([]);
  const [digitalKeyLoginNote, setDigitalKeyLoginNote] = useState('');
  const [pendingDigitalKeyLoginAction, setPendingDigitalKeyLoginAction] = useState(null);
  const [digitalKeyLoginReason, setDigitalKeyLoginReason] = useState('');
  const [recoveryCaseFormOpen, setRecoveryCaseFormOpen] = useState(false);
  const [recoveryCaseType, setRecoveryCaseType] = useState('lost_device');
  const [recoveryCaseSeverity, setRecoveryCaseSeverity] = useState('medium');
  const [recoveryCaseReason, setRecoveryCaseReason] = useState('');
  const [recoveryCaseActions, setRecoveryCaseActions] = useState([]);
  const [pendingRecoveryCaseAction, setPendingRecoveryCaseAction] = useState(null);
  const [recoveryCaseActionNotes, setRecoveryCaseActionNotes] = useState('');
  const [emergencyHoldOpen, setEmergencyHoldOpen] = useState(false);
  const [emergencyHoldReason, setEmergencyHoldReason] = useState('');
  const [digitalKeyDeviceFormOpen, setDigitalKeyDeviceFormOpen] = useState(false);
  const [digitalKeyDeviceLabel, setDigitalKeyDeviceLabel] = useState('');
  const [digitalKeyDeviceType, setDigitalKeyDeviceType] = useState('web_preview');
  const [pendingDigitalKeyDeviceAction, setPendingDigitalKeyDeviceAction] = useState(null);
  const [digitalKeyDeviceReason, setDigitalKeyDeviceReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/idem-identities/admin/identities/${id}`);
      const settledData = (result, fallback) => (result.status === 'fulfilled' ? result.value.data : fallback);
      const [
        devicesResponse,
        sessionsResponse,
        authenticationRequestsResponse,
        assuranceProfileResponse,
        verifiedClaimsResponse,
        consentsResponse,
        relyingPartyProfilesResponse,
        claimSharingTransactionsResponse,
        auditEventsResponse,
        digitalKeyAccountResponse,
        digitalKeyAssurancePolicyResponse,
      ] = await Promise.allSettled([
        api.get(`/idem-identities/admin/identities/${id}/devices`),
        api.get(`/idem-identities/admin/identities/${id}/sessions`),
        api.get(`/idem-identities/admin/identities/${id}/authentication-requests`),
        api.get(`/idem-identities/admin/identities/${id}/assurance-profile`),
        api.get(`/idem-identities/admin/identities/${id}/verified-claims`),
        api.get(`/idem-identities/admin/identities/${id}/consents`),
        api.get('/idem-identities/admin/relying-party-profiles?active_only=true'),
        api.get(`/idem-identities/admin/identities/${id}/claim-sharing-transactions`),
        api.get(`/idem-identities/admin/identities/${id}/events?limit=50`),
        api.get(`/digital-key-accounts/admin/identities/${id}/digital-key-account`),
        api.get(`/digital-key-accounts/admin/identities/${id}/digital-key-assurance-policy`),
      ]);
      setIdentity(data.identity);
      setDevices(settledData(devicesResponse, { devices: [] }).devices || []);
      setSessions(settledData(sessionsResponse, { sessions: [] }).sessions || []);
      setAuthenticationRequests(settledData(authenticationRequestsResponse, { requests: [] }).requests || []);
      setAssuranceProfile(settledData(assuranceProfileResponse, { profile: null }).profile || null);
      setVerifiedClaims(settledData(verifiedClaimsResponse, null) || null);
      setConsents(settledData(consentsResponse, { consents: [] }).consents || []);
      setRelyingPartyProfiles(settledData(relyingPartyProfilesResponse, { profiles: [] }).profiles || []);
      setClaimSharingTransactions(settledData(claimSharingTransactionsResponse, { transactions: [] }).transactions || []);
      setAuditEvents(settledData(auditEventsResponse, { events: [] }).events || []);
      setAuditPagination(settledData(auditEventsResponse, { pagination: { total: 0, limit: 50, offset: 0 } }).pagination || { total: 0, limit: 50, offset: 0 });
      const account = settledData(digitalKeyAccountResponse, { account: null }).account || null;
      setDigitalKeyAccount(account);
      setDigitalKeyAssurancePolicy(settledData(digitalKeyAssurancePolicyResponse, { policy: null }).policy || null);
      if (account) {
        const [
          loginRequestsResponse,
          recoveryCasesResponse,
          digitalKeyDevicesResponse,
        ] = await Promise.allSettled([
          api.get(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/login-requests`),
          api.get(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/recovery-cases`),
          api.get(`/digital-key-accounts/admin/digital-key-accounts/${account.id}/devices`),
        ]);
        setDigitalKeyLoginRequests(settledData(loginRequestsResponse, { requests: [] }).requests || []);
        setDigitalKeyRecoveryCases(settledData(recoveryCasesResponse, { recovery_cases: [] }).recovery_cases || []);
        setDigitalKeyDevices(settledData(digitalKeyDevicesResponse, { devices: [] }).devices || []);
      } else {
        setDigitalKeyLoginRequests([]);
        setDigitalKeyRecoveryCases([]);
        setDigitalKeyDevices([]);
      }
    } catch (requestError) {
      setError(requestError?.response?.status === 404
        ? 'This idem identity could not be found.'
        : 'Identity Detail could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadAuditEvents = async (filters = auditFilters) => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filters.type) params.set('type', filters.type);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const { data } = await api.get(`/idem-identities/admin/identities/${id}/events?${params.toString()}`);
      setAuditEvents(data.events || []);
      setAuditPagination(data.pagination || { total: 0, limit: 50, offset: 0 });
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The identity audit could not be loaded.' });
    } finally {
      setAuditLoading(false);
    }
  };

  const exportAuditEvents = async (format) => {
    setNotice(null);
    try {
      const params = new URLSearchParams({ format });
      if (auditFilters.type) params.set('type', auditFilters.type);
      if (auditFilters.from) params.set('from', auditFilters.from);
      if (auditFilters.to) params.set('to', auditFilters.to);
      const response = await api.get(`/idem-identities/admin/identities/${id}/events/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `idem-identity-audit-${identity?.public_id || id}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The identity audit could not be exported.' });
    }
  };

  const requestLifecycleChange = (status) => {
    setPendingStatus(status);
    setReason('');
    setNotice(null);
  };

  const submitLifecycleChange = async (event) => {
    event.preventDefault();
    if (!reason.trim() || !pendingStatus) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.patch(`/idem-identities/admin/identities/${id}/status`, {
        status: pendingStatus,
        reason: reason.trim(),
      });
      setPendingStatus(null);
      setReason('');
      setNotice({ tone: 'success', text: data.changed ? 'Lifecycle status updated.' : 'Lifecycle status was already current.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The lifecycle status could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const createTestDevice = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/idem-identities/admin/identities/${id}/devices`, { label: deviceLabel, device_type: deviceType });
      setDeviceLabel('');
      setDeviceType('unknown');
      setTestDeviceOpen(false);
      setNotice({ tone: 'success', text: 'Test device added.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The test device could not be added.' });
    } finally {
      setSubmitting(false);
    }
  };

  const requestAction = (kind, record, status = null) => {
    setPendingAction({ kind, record, status });
    setReason('');
    setNotice(null);
  };

  const submitPendingAction = async (event) => {
    event.preventDefault();
    if (!pendingAction || !reason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      if (pendingAction.kind === 'device') {
        await api.patch(`/idem-identities/admin/identities/${id}/devices/${pendingAction.record.id}/status`, {
          status: pendingAction.status,
          reason: reason.trim(),
        });
      } else {
        await api.patch(`/idem-identities/admin/identities/${id}/sessions/${pendingAction.record.id}/revoke`, {
          reason: reason.trim(),
        });
      }
      const label = pendingAction.kind === 'device'
        ? `Device ${pendingAction.status}.`
        : 'Session revoked.';
      setPendingAction(null);
      setReason('');
      setNotice({ tone: 'success', text: label });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The operation could not be completed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const trustedDevices = devices.filter((device) => device.status === 'trusted');

  const startTestAuthentication = async (event) => {
    event.preventDefault();
    if (!authenticationDeviceId) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/idem-identities/admin/identities/${id}/authentication-requests`, {
        device_id: authenticationDeviceId,
        purpose: authenticationPurpose.trim() || 'internal_test',
      });
      setAuthenticationStartOpen(false);
      setAuthenticationDeviceId('');
      setAuthenticationPurpose('internal_test');
      setNotice({ tone: 'success', text: 'Test authentication request started.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The test authentication request could not be started.' });
    } finally {
      setSubmitting(false);
    }
  };

  const approveTestAuthentication = async (request) => {
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.patch(`/idem-identities/admin/identities/${id}/authentication-requests/${request.id}/approve`);
      setNotice({ tone: 'success', text: data.changed ? 'Test authentication approved and session created.' : 'Authentication request was already final.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The test authentication could not be approved.' });
    } finally {
      setSubmitting(false);
    }
  };

  const requestAuthenticationAction = (action, request) => {
    setPendingAuthenticationAction({ action, request });
    setReason('');
    setNotice(null);
  };

  const submitAuthenticationAction = async (event) => {
    event.preventDefault();
    if (!pendingAuthenticationAction) return;
    const { action, request } = pendingAuthenticationAction;
    if (action === 'deny' && !reason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.patch(`/idem-identities/admin/identities/${id}/authentication-requests/${request.id}/${action}`, {
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setPendingAuthenticationAction(null);
      setReason('');
      setNotice({ tone: 'success', text: data.changed ? `Test authentication ${action === 'deny' ? 'denied' : 'expired'}.` : 'Authentication request was already final.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The authentication request could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleConsentClaim = (claimKey) => {
    setConsentRequestedClaims((current) => current.includes(claimKey)
      ? current.filter((key) => key !== claimKey)
      : [...current, claimKey]);
  };

  const selectedRelyingPartyProfile = relyingPartyProfiles.find((profile) => profile.id === consentRelyingPartyProfileId) || null;

  const selectRelyingPartyProfile = (profileId) => {
    setConsentRelyingPartyProfileId(profileId);
    const profile = relyingPartyProfiles.find((item) => item.id === profileId);
    if (profile) {
      const allowedClaims = new Set(profile.allowed_claims || []);
      setConsentRequestedClaims((current) => current.filter((claimKey) => allowedClaims.has(claimKey)));
      if ((profile.allowed_purposes || []).length === 1) setConsentPurpose(profile.allowed_purposes[0]);
    }
  };

  const createDraftConsent = async (event) => {
    event.preventDefault();
    if ((!selectedRelyingPartyProfile && !consentRelyingPartyName.trim()) || !consentPurpose.trim() || consentRequestedClaims.length === 0) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/idem-identities/admin/identities/${id}/consents`, {
        ...(selectedRelyingPartyProfile
          ? { relying_party_profile_id: selectedRelyingPartyProfile.id }
          : { relying_party_name: consentRelyingPartyName.trim() }),
        purpose: consentPurpose.trim(),
        requested_claims: consentRequestedClaims,
        ...(consentExpiresAt ? { expires_at: consentExpiresAt } : {}),
      });
      setConsentDraftOpen(false);
      setConsentRelyingPartyProfileId('');
      setConsentRelyingPartyName('');
      setConsentPurpose('');
      setConsentRequestedClaims([]);
      setConsentExpiresAt('');
      setNotice({ tone: 'success', text: 'Consent draft created.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The consent draft could not be created.' });
    } finally {
      setSubmitting(false);
    }
  };

  const grantConsent = async (consent) => {
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.patch(`/idem-identities/admin/identities/${id}/consents/${consent.id}/grant`);
      const skipped = data.skipped_claims?.length
        ? ` ${data.skipped_claims.map((claim) => typeof claim === 'string' ? claim : `${claim.claim_key} (${claim.reason})`).join(', ')} skipped.`
        : '';
      setNotice({ tone: 'success', text: data.changed ? `Consent granted.${skipped}` : 'Consent was already granted.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The consent could not be granted.' });
    } finally {
      setSubmitting(false);
    }
  };

  const requestConsentAction = (action, consent) => {
    setPendingConsentAction({ action, consent });
    setConsentReason('');
    setNotice(null);
  };

  const submitConsentAction = async (event) => {
    event.preventDefault();
    if (!pendingConsentAction || (pendingConsentAction.action === 'revoke' && !consentReason.trim())) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.patch(`/idem-identities/admin/identities/${id}/consents/${pendingConsentAction.consent.id}/${pendingConsentAction.action}`, {
        ...(pendingConsentAction.action === 'revoke' ? { reason: consentReason.trim() } : {}),
      });
      const completedAction = pendingConsentAction.action;
      setPendingConsentAction(null);
      setConsentReason('');
      setNotice({ tone: 'success', text: completedAction === 'revoke' ? 'Consent revoked.' : 'Consent expired.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The consent could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const prepareClaimSharingTransaction = async () => {
    if (!claimSharingConsentId) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/idem-identities/admin/identities/${id}/claim-sharing-transactions`, { consent_id: claimSharingConsentId });
      setClaimSharingConsentId('');
      setNotice({
        tone: data.transaction?.status === 'blocked' ? 'error' : 'success',
        text: data.transaction?.status === 'blocked'
          ? `Claim sharing was blocked: ${data.transaction.blocked_reason || 'no currently verified claims are available'}.`
          : 'Claim sharing transaction prepared. No claims were shared externally.',
      });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The claim sharing transaction could not be prepared.' });
    } finally {
      setSubmitting(false);
    }
  };

  const completeClaimSharingTransaction = async (transaction) => {
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.patch(`/idem-identities/admin/identities/${id}/claim-sharing-transactions/${transaction.id}/complete`);
      setNotice({
        tone: data.transaction?.status === 'blocked' ? 'error' : 'success',
        text: data.transaction?.status === 'blocked'
          ? `Claim sharing was blocked: ${data.transaction.blocked_reason || 'current rules no longer allow it'}.`
          : data.changed ? 'Claim sharing transaction completed. No claims were shared externally.' : 'Claim sharing transaction was already completed.',
      });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The claim sharing transaction could not be completed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitClaimSharingCancellation = async (event) => {
    event.preventDefault();
    if (!pendingClaimSharingCancellation) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.patch(`/idem-identities/admin/identities/${id}/claim-sharing-transactions/${pendingClaimSharingCancellation.id}/cancel`, {
        ...(claimSharingCancellationReason.trim() ? { reason: claimSharingCancellationReason.trim() } : {}),
      });
      setPendingClaimSharingCancellation(null);
      setClaimSharingCancellationReason('');
      setNotice({ tone: 'success', text: 'Claim sharing transaction cancelled. No claims were shared externally.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The claim sharing transaction could not be cancelled.' });
    } finally {
      setSubmitting(false);
    }
  };

  const createDigitalKeyAccount = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/identities/${id}/digital-key-account`);
      setNotice({ tone: 'success', text: data.created ? 'Digital key account created. This remains internal-only.' : 'A digital key account already exists for this identity.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The digital key account could not be created.' });
    } finally {
      setSubmitting(false);
    }
  };

  const evaluateDigitalKeyAccount = async () => {
    if (!digitalKeyAccount) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/evaluate-eligibility`);
      setNotice({ tone: 'success', text: `Eligibility evaluated: ${String(data.account?.eligibility_status || 'needs_review').replaceAll('_', ' ')}.` });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'Digital key eligibility could not be evaluated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const generateDigitalKeyActivationLink = async () => {
    if (!digitalKeyAccount) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/activation-links`);
      const activationUrl = data.activation_url || data.activation_link?.activation_url || '';
      setDigitalKeyActivationLink(activationUrl);
      if (activationUrl && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${window.location.origin}${activationUrl}`);
      }
      setNotice({ tone: 'success', text: 'Lab activation link generated for this digital key account.' });
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The lab activation link could not be generated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const runDigitalKeyAction = async (action, reasonValue = null) => {
    if (!digitalKeyAccount) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/${action}`, reasonValue ? { reason: reasonValue } : undefined);
      const label = {
        activate: 'activated',
        suspend: 'suspended',
        reactivate: 'reactivated',
        revoke: 'revoked',
      }[action] || action;
      setNotice({ tone: 'success', text: data.changed ? `Digital key account ${label}.` : 'Digital key account was already current.' });
      setPendingDigitalKeyAction(null);
      setDigitalKeyReason('');
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The digital key account could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDigitalKeyAction = async (event) => {
    event.preventDefault();
    if (!pendingDigitalKeyAction || !digitalKeyReason.trim()) return;
    await runDigitalKeyAction(pendingDigitalKeyAction, digitalKeyReason.trim());
  };

  const toggleDigitalKeyLoginClaim = (claimKey) => {
    setDigitalKeyLoginClaims((current) => current.includes(claimKey)
      ? current.filter((key) => key !== claimKey)
      : [...current, claimKey]);
  };

  const createDigitalKeyLoginRequest = async (event) => {
    event.preventDefault();
    if (!digitalKeyAccount || digitalKeyAccount.status !== 'active') return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/login-requests`, {
        requested_assurance: digitalKeyLoginAssurance,
        requested_claims: digitalKeyLoginClaims,
        ...(digitalKeyLoginNote.trim() ? { note: digitalKeyLoginNote.trim() } : {}),
      });
      setDigitalKeyLoginFormOpen(false);
      setDigitalKeyLoginAssurance('internal_foundation');
      setDigitalKeyLoginClaims([]);
      setDigitalKeyLoginNote('');
      setNotice({ tone: 'success', text: 'Internal digital key login request created. No tokens or authorization codes were issued.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The internal login request could not be created.' });
    } finally {
      setSubmitting(false);
    }
  };

  const runDigitalKeyLoginAction = async (request, action, reasonValue = null) => {
    setSubmitting(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/digital-key-accounts/admin/digital-key-login-requests/${request.id}/${action}`, reasonValue ? { reason: reasonValue } : undefined);
      const label = {
        approve: 'approved',
        deny: 'denied',
        expire: 'expired',
        cancel: 'cancelled',
      }[action] || 'updated';
      setPendingDigitalKeyLoginAction(null);
      setDigitalKeyLoginReason('');
      setNotice({ tone: 'success', text: data.changed ? `Internal login request ${label}. No tokens or authorization codes were issued.` : 'The login request was already terminal.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The internal login request could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDigitalKeyLoginAction = async (event) => {
    event.preventDefault();
    if (!pendingDigitalKeyLoginAction || !digitalKeyLoginReason.trim()) return;
    await runDigitalKeyLoginAction(pendingDigitalKeyLoginAction.request, pendingDigitalKeyLoginAction.action, digitalKeyLoginReason.trim());
  };

  const createDigitalKeyDevice = async (event) => {
    event.preventDefault();
    if (!digitalKeyAccount || !digitalKeyDeviceLabel.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/devices`, {
        device_label: digitalKeyDeviceLabel.trim(),
        device_type: digitalKeyDeviceType,
        binding_method: 'internal_admin',
      });
      setDigitalKeyDeviceFormOpen(false);
      setDigitalKeyDeviceLabel('');
      setDigitalKeyDeviceType('web_preview');
      setNotice({ tone: 'success', text: 'Internal Digital Key device added as pending.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The Digital Key device could not be added.' });
    } finally {
      setSubmitting(false);
    }
  };

  const runDigitalKeyDeviceAction = async (device, action, reasonValue = null) => {
    setSubmitting(true);
    setNotice(null);
    try {
      const body = ['block', 'remove'].includes(action) ? { reason: reasonValue } : undefined;
      await api.post(`/digital-key-accounts/admin/digital-key-devices/${device.id}/${action}`, body);
      setPendingDigitalKeyDeviceAction(null);
      setDigitalKeyDeviceReason('');
      const label = { activate: 'activated', block: 'blocked', remove: 'removed' }[action] || 'updated';
      setNotice({ tone: 'success', text: `Digital Key device ${label}.` });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The Digital Key device could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDigitalKeyDeviceAction = async (event) => {
    event.preventDefault();
    if (!pendingDigitalKeyDeviceAction || !digitalKeyDeviceReason.trim()) return;
    await runDigitalKeyDeviceAction(pendingDigitalKeyDeviceAction.device, pendingDigitalKeyDeviceAction.action, digitalKeyDeviceReason.trim());
  };

  const toggleRecoveryCaseAction = (action) => {
    setRecoveryCaseActions((current) => current.includes(action)
      ? current.filter((item) => item !== action)
      : [...current, action]);
  };

  const createRecoveryCase = async (event) => {
    event.preventDefault();
    if (!digitalKeyAccount || !recoveryCaseReason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/recovery-cases`, {
        case_type: recoveryCaseType,
        severity: recoveryCaseSeverity,
        reason: recoveryCaseReason.trim(),
        requested_actions: recoveryCaseActions,
      });
      setRecoveryCaseFormOpen(false);
      setRecoveryCaseType('lost_device');
      setRecoveryCaseSeverity('medium');
      setRecoveryCaseReason('');
      setRecoveryCaseActions([]);
      setNotice({ tone: 'success', text: 'Internal recovery case opened. Requested actions are recorded for review only.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The recovery case could not be opened.' });
    } finally {
      setSubmitting(false);
    }
  };

  const runRecoveryCaseAction = async (recoveryCase, action, notes = null) => {
    const endpoint = {
      in_review: 'in-review',
      resolved: 'resolve',
      rejected: 'reject',
      cancelled: 'cancel',
    }[action];
    if (!endpoint) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const body = action === 'cancelled'
        ? { reason: notes }
        : action === 'resolved' || action === 'rejected'
          ? { resolution_notes: notes }
          : undefined;
      await api.post(`/digital-key-accounts/admin/digital-key-recovery-cases/${recoveryCase.id}/${endpoint}`, body);
      setPendingRecoveryCaseAction(null);
      setRecoveryCaseActionNotes('');
      setNotice({ tone: 'success', text: `Recovery case ${action.replaceAll('_', ' ')}.` });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The recovery case could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitRecoveryCaseAction = async (event) => {
    event.preventDefault();
    if (!pendingRecoveryCaseAction) return;
    if (pendingRecoveryCaseAction.action === 'in_review') {
      await runRecoveryCaseAction(pendingRecoveryCaseAction.recoveryCase, 'in_review');
      return;
    }
    if (!recoveryCaseActionNotes.trim()) return;
    await runRecoveryCaseAction(pendingRecoveryCaseAction.recoveryCase, pendingRecoveryCaseAction.action, recoveryCaseActionNotes.trim());
  };

  const applyEmergencyHold = async (event) => {
    event.preventDefault();
    if (!digitalKeyAccount || !emergencyHoldReason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-accounts/${digitalKeyAccount.id}/emergency-hold`, { reason: emergencyHoldReason.trim() });
      setEmergencyHoldOpen(false);
      setEmergencyHoldReason('');
      setNotice({ tone: 'success', text: 'Emergency hold applied. The internal Digital Key Account is suspended.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The emergency hold could not be applied.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading identity…</div>;
  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!identity) return null;
  const identityRef = identity.public_id || identity.id;

  const isActive = identity.status === 'active';
  const isSuspended = identity.status === 'suspended';
  const shareableConsents = consents.filter((consent) => consent.status === 'granted' && (!consent.expires_at || new Date(consent.expires_at) > new Date()));
  const claimByKey = (claimKey) => (verifiedClaims?.claims || []).find((claim) => claim.claim_key === claimKey);
  const digitalKeyLoginClaimOptions = (verifiedClaims?.claims || []).filter((claim) => digitalKeyLoginClaimKeys.has(claim.claim_key));
  const latestDigitalKeyLoginRequest = digitalKeyLoginRequests[0] || null;
  const demoSteps = [
    { label: 'Source verification approved', complete: claimByKey('identity_verified')?.claim_status === 'verified', detail: 'An approved KYC or IDV source supports the identity.', tab: 'assurance_claims' },
    { label: 'idem identity created', complete: Boolean(identity.id), detail: 'An internal idem identity record exists.', tab: 'overview' },
    { label: 'Digital key account created', complete: Boolean(digitalKeyAccount), detail: 'An internal-only lifecycle record exists. It does not enable public authentication.', tab: 'overview' },
    { label: 'Digital key trusted device active', complete: digitalKeyDevices.some((device) => device.status === 'active'), detail: 'At least one active trusted device is linked to the Digital Key Account.', tab: 'overview' },
    { label: 'Internal authentication policy ready', complete: digitalKeyAssurancePolicy?.status === 'internal_authentication_ready', detail: 'The computed internal policy has all currently required evidence, account, device, and recovery checks.', tab: 'overview' },
    { label: 'Digital key login request prepared', complete: digitalKeyLoginRequests.length > 0, detail: 'An internal sandbox login request has been created for the active digital key account.', tab: 'overview' },
    { label: 'Digital key login request approved', complete: digitalKeyLoginRequests.some((request) => request.status === 'approved'), detail: 'An internal sandbox request was approved without issuing tokens or authorization codes.', tab: 'overview' },
    { label: 'Device-bound approval available', complete: digitalKeyLoginRequests.some((request) => Boolean(request.approved_device_id)), detail: 'A login request can be approved using an active trusted device in the internal authenticator preview.', tab: 'overview' },
    { label: 'Recovery case available', complete: digitalKeyRecoveryCases.length > 0, detail: 'An internal recovery or security case exists for the digital key account.', tab: 'overview' },
    { label: 'Emergency hold tested', complete: auditEvents.some((event) => event.action === 'digital_key.recovery_case.emergency_hold_applied'), detail: 'The internal Digital Key Account can be suspended through a controlled emergency hold.', tab: 'overview' },
    { label: 'Assurance profile available', complete: Boolean(assuranceProfile), detail: 'An internal readiness summary is available.', tab: 'assurance_claims' },
    { label: 'Verified claims available', complete: (verifiedClaims?.claims || []).length > 0, detail: 'Safe claims are derived from internal evidence.', tab: 'assurance_claims' },
    { label: 'Trusted device available', complete: claimByKey('trusted_device_available')?.claim_status === 'verified', detail: 'At least one active trusted device is linked.', tab: 'devices_sessions' },
    { label: 'Authentication session available', complete: sessions.some((session) => session.status === 'active'), detail: 'An active internal identity session exists.', tab: 'devices_sessions' },
    { label: 'Relying party profile available', complete: relyingPartyProfiles.length > 0, detail: 'At least one active internal relying party profile is available.', tab: 'consent_sharing' },
    { label: 'Consent granted', complete: consents.some((consent) => consent.status === 'granted'), detail: 'A current internal consent record has been granted.', tab: 'consent_sharing' },
    { label: 'Claim sharing transaction prepared', complete: claimSharingTransactions.some((transaction) => ['prepared', 'completed'].includes(transaction.status)), detail: 'A preview-only transaction has been prepared.', tab: 'consent_sharing' },
    { label: 'Claim sharing transaction completed', complete: claimSharingTransactions.some((transaction) => transaction.status === 'completed'), detail: 'A preview-only transaction has been completed.', tab: 'consent_sharing' },
    { label: 'Audit trail available', complete: auditPagination.total > 0, detail: 'Sanitized internal audit events are available for review.', tab: 'audit' },
  ];

  return (
    <div id="identity-detail" data-testid="identity-detail-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Identity Detail" showDashboard={false}/>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/internal/identities" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-black">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5}/> Identity Operations
          </Link>
          <p className="mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">Identity Detail</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl font-bold tracking-tight">{identity.public_id}</h1>
            <IdentityStatus status={identity.status}/>
          </div>
          <p className="mt-2 text-sm text-zinc-600">Internal admin-only identity record.</p>
        </div>
      </div>

      {notice && (
        <div className={`mb-6 border px-4 py-3 text-sm ${notice.tone === 'success'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
          : 'border-red-300 bg-red-50 text-red-700'}`}>
          {notice.text}
        </div>
      )}

      <IdentityDetailTabs activeTab={activeTab} onChange={setActiveTab}/>

      {activeTab === 'overview' && (
        <div id="identity-panel-overview" role="tabpanel" aria-labelledby="identity-tab-overview">
      <section className="mb-6 border border-zinc-200 bg-white p-5" aria-labelledby="internal-demo-flow-title">
        <div>
          <h2 id="internal-demo-flow-title" className="font-display text-lg font-semibold">Internal demo flow</h2>
          <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal identity operations only. This demo does not represent FAS approval, CSAM readiness, eIDAS certification, official recognition, or an official LoA.</p>
        </div>
        <ol className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {demoSteps.map((step) => (
            <li key={step.label}>
              <button type="button" onClick={() => setActiveTab(step.tab)} className={`block w-full border px-3 py-2.5 text-left transition-colors ${step.complete ? 'border-emerald-200 bg-emerald-50/40 hover:border-emerald-300' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}>
                <div className="flex items-start gap-2">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${step.complete ? 'bg-emerald-500' : 'bg-zinc-300'}`} aria-hidden="true"/>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800">{step.label}</p>
                    <p className="mt-0.5 text-xs leading-5 text-zinc-600">{step.detail}</p>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-6 border border-zinc-200 bg-white p-5" aria-labelledby="digital-key-account-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/>
              <h2 id="digital-key-account-title" className="font-display text-lg font-semibold">Digital Key Account</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal lifecycle foundation linked to this idem identity. It does not create public login, OIDC, FAS/CSAM integration, token issuance, official LoA, or regulated identifier processing.</p>
          </div>
          {digitalKeyAccount && (
            <div className="flex flex-wrap gap-2">
              <DigitalKeyStatus status={digitalKeyAccount.status}/>
              <DigitalKeyEligibilityStatus status={digitalKeyAccount.eligibility_status}/>
            </div>
          )}
        </div>

        {!digitalKeyAccount ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 pt-4">
            <p className="max-w-2xl text-sm text-zinc-600">No digital key account linked yet. Creating one is internal-only and does not enable public authentication or protocol functionality.</p>
            <button type="button" onClick={createDigitalKeyAccount} disabled={submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">
              Create digital key account
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account ID</p>
                <p className="mt-1 break-all font-mono text-xs text-zinc-700">{digitalKeyAccount.id}</p>
              </div>
              <div className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <DigitalKeyStatus status={digitalKeyAccount.status}/>
                  <DigitalKeyEligibilityStatus status={digitalKeyAccount.eligibility_status}/>
                </div>
              </div>
              <div className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Policy summary</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <DigitalKeyAssurancePolicyStatus status={digitalKeyAssurancePolicy?.status}/>
                  <span className="text-xs text-zinc-600">
                    {digitalKeyAssurancePolicy?.can_authenticate ? 'Can authenticate' : 'Cannot authenticate'} · max {String(digitalKeyAssurancePolicy?.max_internal_assurance || 'not_ready').replaceAll('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Recent login requests</p>
                <p className="mt-1 text-sm text-zinc-700">{digitalKeyLoginRequests.length} request(s)</p>
                <p className="mt-1 text-xs text-zinc-500">{latestDigitalKeyLoginRequest ? `${String(latestDigitalKeyLoginRequest.status || 'pending').replaceAll('_', ' ')} · ${formatDate(latestDigitalKeyLoginRequest.updated_at || latestDigitalKeyLoginRequest.created_at)}` : 'No login requests yet'}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-100 pt-4">
              <Link to="/internal/digital-key-accounts" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open digital key accounts</Link>
              <Link to={`/internal/oidc-lab?identity_id=${encodeURIComponent(identityRef)}${digitalKeyAccount ? `&account_id=${encodeURIComponent(digitalKeyAccount.id)}` : ''}`} className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Start lab login</Link>
              <button type="button" onClick={generateDigitalKeyActivationLink} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400 disabled:opacity-50">
                Generate lab activation link
              </button>
              <Link to="/internal/oidc-lab" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open OIDC lab</Link>
              <Link to="/internal/authenticator-preview" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open authenticator preview</Link>
            </div>
            {digitalKeyActivationLink && (
              <div className="mt-3 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                <p className="font-medium">Lab activation link ready</p>
                <p className="mt-1 break-all font-mono">{digitalKeyActivationLink}</p>
              </div>
            )}

            <dl className="mt-4 grid gap-x-6 md:grid-cols-2">
              <SummaryRow label="Account ID"><span className="font-mono text-xs">{digitalKeyAccount.id}</span></SummaryRow>
              <SummaryRow label="Created at">{formatDate(digitalKeyAccount.created_at)}</SummaryRow>
              <SummaryRow label="Updated at">{formatDate(digitalKeyAccount.updated_at)}</SummaryRow>
              <SummaryRow label="Activated at">{formatDate(digitalKeyAccount.activated_at)}</SummaryRow>
              <SummaryRow label="Suspended at">{formatDate(digitalKeyAccount.suspended_at)}</SummaryRow>
              <SummaryRow label="Revoked at">{formatDate(digitalKeyAccount.revoked_at)}</SummaryRow>
            </dl>

            <div className="mt-4 border-t border-zinc-100 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium text-zinc-800">Eligibility checks</h3>
                  <p className="mt-1 text-xs text-zinc-500">Trusted device availability is advisory in this internal v0.1 model.</p>
                </div>
                <button type="button" onClick={evaluateDigitalKeyAccount} disabled={submitting || digitalKeyAccount.status === 'revoked'} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:cursor-not-allowed disabled:opacity-50">
                  Evaluate eligibility
                </button>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {Object.entries(digitalKeyAccount.eligibility_checks || {}).filter(([key]) => key !== 'limitations').map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-3 border border-zinc-100 px-3 py-2 text-sm">
                    <span className="text-zinc-700">{digitalKeyCheckLabel(key)}</span>
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${value ? 'text-emerald-700' : 'text-zinc-500'}`}>{value ? 'Yes' : 'No'}</span>
                  </div>
                ))}
              </div>
              {(digitalKeyAccount.eligibility_checks?.limitations || []).length > 0 && (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-zinc-500">
                  {digitalKeyAccount.eligibility_checks.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}
                </ul>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-100 pt-4">
              {['draft', 'suspended'].includes(digitalKeyAccount.status) && (
                <button type="button" onClick={() => runDigitalKeyAction(digitalKeyAccount.status === 'suspended' ? 'reactivate' : 'activate')} disabled={submitting || digitalKeyAccount.eligibility_status !== 'eligible'} className="border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
                  {digitalKeyAccount.status === 'suspended' ? 'Reactivate' : 'Activate'}
                </button>
              )}
              {digitalKeyAccount.status === 'active' && (
                <button type="button" onClick={() => { setPendingDigitalKeyAction('suspend'); setDigitalKeyReason(''); }} disabled={submitting} className="border border-amber-300 px-3 py-2 text-sm text-amber-800 hover:border-amber-500 disabled:opacity-50">
                  Suspend
                </button>
              )}
              {['draft', 'active', 'suspended'].includes(digitalKeyAccount.status) && (
                <button type="button" onClick={() => { setPendingDigitalKeyAction('revoke'); setDigitalKeyReason(''); }} disabled={submitting} className="border border-red-200 px-3 py-2 text-sm text-red-700 hover:border-red-400 disabled:opacity-50">
                  Revoke
                </button>
              )}
            </div>

            {digitalKeyAccount.status === 'revoked' && (
              <p className="mt-4 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">Revoked digital key accounts are terminal in this internal version.</p>
            )}

            {pendingDigitalKeyAction && (
              <form onSubmit={submitDigitalKeyAction} className="mt-4 border-t border-zinc-100 pt-4">
                <p className="text-sm font-medium text-zinc-800">Confirm digital key account {pendingDigitalKeyAction}</p>
                <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="digital-key-account-reason">Reason</label>
                <textarea id="digital-key-account-reason" value={digitalKeyReason} onChange={(event) => setDigitalKeyReason(event.target.value)} required maxLength={500} rows={3}
                  className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                <div className="mt-3 flex justify-end gap-2">
                  <button type="button" onClick={() => { setPendingDigitalKeyAction(null); setDigitalKeyReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                  <button type="submit" disabled={!digitalKeyReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
                </div>
              </form>
            )}

            <div className="mt-6 border-t border-zinc-100 pt-5" aria-labelledby="digital-key-recovery-title">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 id="digital-key-recovery-title" className="text-base font-semibold text-zinc-900">Recovery &amp; Revocation Controls</h3>
                  <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal recovery and security case workflow for future digital key operations. This does not create public recovery, OIDC, FAS/CSAM integration, official LoA, or regulated identifier processing.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setRecoveryCaseFormOpen((open) => !open); setPendingRecoveryCaseAction(null); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">
                    {recoveryCaseFormOpen ? 'Close form' : 'Open recovery case'}
                  </button>
                  {digitalKeyAccount.status === 'active' && (
                    <button type="button" onClick={() => { setEmergencyHoldOpen((open) => !open); setEmergencyHoldReason(''); }} disabled={submitting} className="border border-amber-300 px-3 py-2 text-sm text-amber-800 hover:border-amber-500 disabled:opacity-50">
                      Apply emergency hold
                    </button>
                  )}
                </div>
              </div>

              {recoveryCaseFormOpen && (
                <form onSubmit={createRecoveryCase} className="mt-4 border-t border-zinc-100 pt-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Case type</span>
                      <select value={recoveryCaseType} onChange={(event) => setRecoveryCaseType(event.target.value)} className="mt-1 block w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                        {recoveryCaseTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Severity</span>
                      <select value={recoveryCaseSeverity} onChange={(event) => setRecoveryCaseSeverity(event.target.value)} className="mt-1 block w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </label>
                  </div>
                  <label className="mt-4 block text-sm text-zinc-700">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reason</span>
                    <textarea value={recoveryCaseReason} onChange={(event) => setRecoveryCaseReason(event.target.value)} required maxLength={500} rows={3} className="mt-1 block w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                  </label>
                  <fieldset className="mt-4">
                    <legend className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested actions</legend>
                    <p className="mt-1 text-xs text-zinc-500">Recorded as case intent only. No device or session action is performed automatically.</p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {recoveryActionLabels.map(([value, label]) => (
                        <label key={value} className="flex items-center gap-2 border border-zinc-100 px-3 py-2 text-sm text-zinc-700">
                          <input type="checkbox" checked={recoveryCaseActions.includes(value)} onChange={() => toggleRecoveryCaseAction(value)} className="h-3.5 w-3.5 border-zinc-300"/>
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="mt-4 flex justify-end gap-2">
                    <button type="button" onClick={() => { setRecoveryCaseFormOpen(false); setRecoveryCaseReason(''); setRecoveryCaseActions([]); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!recoveryCaseReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Open case</button>
                  </div>
                </form>
              )}

              {emergencyHoldOpen && (
                <form onSubmit={applyEmergencyHold} className="mt-4 border border-amber-200 bg-amber-50/50 p-4">
                  <div className="flex items-start gap-2 text-sm text-amber-900">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5}/>
                    <p>Emergency hold suspends the internal Digital Key Account only. It does not revoke the idem identity, delete devices, issue tokens, or contact FAS/CSAM.</p>
                  </div>
                  <label className="mt-3 block text-sm text-zinc-700">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reason</span>
                    <textarea value={emergencyHoldReason} onChange={(event) => setEmergencyHoldReason(event.target.value)} required maxLength={500} rows={3} className="mt-1 block w-full resize-y border border-amber-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"/>
                  </label>
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" onClick={() => { setEmergencyHoldOpen(false); setEmergencyHoldReason(''); }} disabled={submitting} className="border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!emergencyHoldReason.trim() || submitting} className="border border-amber-500 bg-amber-100 px-3 py-2 text-sm text-amber-900 hover:bg-amber-200 disabled:opacity-50">Apply emergency hold</button>
                  </div>
                </form>
              )}

              {digitalKeyRecoveryCases.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="py-2 pr-3 font-medium">Case ID</th>
                        <th className="py-2 pr-3 font-medium">Type</th>
                        <th className="py-2 pr-3 font-medium">Severity</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium">Actions</th>
                        <th className="py-2 pr-3 font-medium">Created</th>
                        <th className="py-2 pr-3 font-medium">Reviewed</th>
                        <th className="py-2 pr-3 font-medium">Finalized</th>
                        <th className="py-2 font-medium">Controls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digitalKeyRecoveryCases.map((recoveryCase) => (
                        <tr key={recoveryCase.id} className="border-b border-zinc-100 last:border-b-0">
                          <td className="max-w-40 truncate py-3 pr-3 font-mono text-xs text-zinc-600" title={recoveryCase.id}>{recoveryCase.id}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(recoveryCase.case_type || '').replaceAll('_', ' ')}</td>
                          <td className="py-3 pr-3"><RecoveryCaseSeverity severity={recoveryCase.severity}/></td>
                          <td className="py-3 pr-3"><RecoveryCaseStatus status={recoveryCase.status}/></td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{recoveryCase.requested_actions?.length || 0}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(recoveryCase.created_at)}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(recoveryCase.reviewed_at)}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(recoveryCase.resolved_at || recoveryCase.cancelled_at)}</td>
                          <td className="py-3">
                            {['open', 'in_review'].includes(recoveryCase.status) && (
                              <div className="flex flex-wrap gap-1.5">
                                {recoveryCase.status === 'open' && <button type="button" onClick={() => runRecoveryCaseAction(recoveryCase, 'in_review')} disabled={submitting} className="border border-amber-300 px-2 py-1 text-xs text-amber-800 hover:border-amber-500 disabled:opacity-50">Mark in review</button>}
                                <button type="button" onClick={() => { setPendingRecoveryCaseAction({ action: 'resolved', recoveryCase }); setRecoveryCaseActionNotes(''); }} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Resolve</button>
                                <button type="button" onClick={() => { setPendingRecoveryCaseAction({ action: 'rejected', recoveryCase }); setRecoveryCaseActionNotes(''); }} disabled={submitting} className="border border-red-200 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50">Reject</button>
                                <button type="button" onClick={() => { setPendingRecoveryCaseAction({ action: 'cancelled', recoveryCase }); setRecoveryCaseActionNotes(''); }} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500">No internal recovery cases yet.</p>
              )}

              {pendingRecoveryCaseAction && (
                <form onSubmit={submitRecoveryCaseAction} className="mt-4 border-t border-zinc-100 pt-4">
                  <p className="text-sm font-medium text-zinc-800">Confirm recovery case {pendingRecoveryCaseAction.action.replaceAll('_', ' ')}</p>
                  <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="digital-key-recovery-case-notes">
                    {pendingRecoveryCaseAction.action === 'cancelled' ? 'Cancellation reason' : 'Resolution notes'}
                  </label>
                  <textarea id="digital-key-recovery-case-notes" value={recoveryCaseActionNotes} onChange={(event) => setRecoveryCaseActionNotes(event.target.value)} required maxLength={500} rows={3}
                    className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" onClick={() => { setPendingRecoveryCaseAction(null); setRecoveryCaseActionNotes(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!recoveryCaseActionNotes.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
                  </div>
                </form>
              )}
            </div>

            <div className="mt-6 border-t border-zinc-100 pt-5" aria-labelledby="digital-key-devices-title">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 id="digital-key-devices-title" className="text-base font-semibold text-zinc-900">Digital Key Trusted Devices</h3>
                  <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal device binding preview only. This does not create a real mobile authenticator, push notification, biometric unlock, OIDC token, FAS/CSAM integration, official LoA, or regulated identifier processing.</p>
                </div>
                <button type="button" onClick={() => { setDigitalKeyDeviceFormOpen((open) => !open); setPendingDigitalKeyDeviceAction(null); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">
                  {digitalKeyDeviceFormOpen ? 'Close form' : 'Add internal device'}
                </button>
              </div>

              {digitalKeyDeviceFormOpen && (
                <form onSubmit={createDigitalKeyDevice} className="mt-4 border-t border-zinc-100 pt-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device label</span>
                      <input value={digitalKeyDeviceLabel} onChange={(event) => setDigitalKeyDeviceLabel(event.target.value)} required maxLength={120} placeholder="Lucas MacBook internal preview" className="mt-1 block w-full border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                    </label>
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device type</span>
                      <select value={digitalKeyDeviceType} onChange={(event) => setDigitalKeyDeviceType(event.target.value)} className="mt-1 block w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                        <option value="web_preview">Web preview</option>
                        <option value="mobile_preview">Mobile preview</option>
                        <option value="desktop_preview">Desktop preview</option>
                        <option value="security_key_preview">Security key preview</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                  </div>
                  <p className="mt-3 text-xs text-zinc-500">Binding method: Internal admin</p>
                  <div className="mt-4 flex justify-end gap-2">
                    <button type="button" onClick={() => { setDigitalKeyDeviceFormOpen(false); setDigitalKeyDeviceLabel(''); setDigitalKeyDeviceType('web_preview'); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!digitalKeyDeviceLabel.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Add device</button>
                  </div>
                </form>
              )}

              {digitalKeyDevices.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr><th className="py-2 pr-3 font-medium">Device</th><th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Binding</th><th className="py-2 pr-3 font-medium">Last used</th><th className="py-2 pr-3 font-medium">Created</th><th className="py-2 font-medium">Actions</th></tr>
                    </thead>
                    <tbody>
                      {digitalKeyDevices.map((device) => (
                        <tr key={device.id} className="border-b border-zinc-100 last:border-b-0">
                          <td className="py-3 pr-3 text-sm text-zinc-800">{device.device_label}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(device.device_type || '').replaceAll('_', ' ')}</td>
                          <td className="py-3 pr-3"><DigitalKeyDeviceStatus status={device.status}/></td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(device.binding_method || '').replaceAll('_', ' ')}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(device.last_used_at)}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(device.created_at)}</td>
                          <td className="py-3">
                            {device.status !== 'removed' && <div className="flex flex-wrap gap-1.5">
                              {device.status === 'pending' && <button type="button" onClick={() => runDigitalKeyDeviceAction(device, 'activate')} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Activate</button>}
                              {device.status === 'active' && <button type="button" onClick={() => { setPendingDigitalKeyDeviceAction({ action: 'block', device }); setDigitalKeyDeviceReason(''); }} disabled={submitting} className="border border-amber-300 px-2 py-1 text-xs text-amber-800 hover:border-amber-500 disabled:opacity-50">Block</button>}
                              <button type="button" onClick={() => { setPendingDigitalKeyDeviceAction({ action: 'remove', device }); setDigitalKeyDeviceReason(''); }} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Remove</button>
                            </div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="mt-4 text-sm text-zinc-500">No Digital Key trusted devices yet.</p>}

              {pendingDigitalKeyDeviceAction && (
                <form onSubmit={submitDigitalKeyDeviceAction} className="mt-4 border-t border-zinc-100 pt-4">
                  <p className="text-sm font-medium text-zinc-800">Confirm device {pendingDigitalKeyDeviceAction.action}</p>
                  <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="digital-key-device-reason">Reason</label>
                  <textarea id="digital-key-device-reason" value={digitalKeyDeviceReason} onChange={(event) => setDigitalKeyDeviceReason(event.target.value)} required maxLength={500} rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" onClick={() => { setPendingDigitalKeyDeviceAction(null); setDigitalKeyDeviceReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!digitalKeyDeviceReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
                  </div>
                </form>
              )}
            </div>

            <div className="mt-6 border-t border-zinc-100 pt-5" aria-labelledby="digital-key-login-requests-title">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 id="digital-key-login-requests-title" className="text-base font-semibold text-zinc-900">Digital Key Login Requests</h3>
                  <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal sandbox only. Login requests do not create public login sessions, OIDC authorization codes, tokens, FAS/CSAM integration, official LoA, or regulated identifier processing.</p>
                </div>
                <div className="flex flex-wrap gap-2">
              <Link to="/internal/authenticator-preview" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open authenticator preview</Link>
              <Link to="/id/authenticator" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open idem app preview</Link>
                  <Link to={`/internal/oidc-lab?identity_id=${encodeURIComponent(identityRef)}&account_id=${encodeURIComponent(digitalKeyAccount.id)}`} className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Start lab login</Link>
                  {digitalKeyAccount.status === 'active' && (
                    <button type="button" onClick={() => { setDigitalKeyLoginFormOpen((open) => !open); setPendingDigitalKeyLoginAction(null); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">
                      {digitalKeyLoginFormOpen ? 'Close form' : 'Create internal login request'}
                    </button>
                  )}
                </div>
              </div>

              {digitalKeyAccount.status !== 'active' && (
                <p className="mt-4 border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">Activate a Digital Key Account before creating internal login requests.</p>
              )}

              {digitalKeyLoginFormOpen && digitalKeyAccount.status === 'active' && (
                <form onSubmit={createDigitalKeyLoginRequest} className="mt-4 border-t border-zinc-100 pt-4">
                  <div className="grid gap-3 md:grid-cols-[15rem_minmax(0,1fr)]">
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested assurance</span>
                      <select value={digitalKeyLoginAssurance} onChange={(event) => setDigitalKeyLoginAssurance(event.target.value)} className="mt-1 block w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                        <option value="internal_foundation">Internal foundation</option>
                        <option value="internal_evidence_ready">Internal evidence ready</option>
                        <option value="internal_authentication_ready">Internal authentication ready</option>
                      </select>
                    </label>
                    <label className="block text-sm text-zinc-700">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Internal note (optional)</span>
                      <input value={digitalKeyLoginNote} onChange={(event) => setDigitalKeyLoginNote(event.target.value)} maxLength={500} placeholder="Internal simulation only" className="mt-1 block w-full border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                    </label>
                  </div>
                  <fieldset className="mt-4">
                    <legend className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested safe claims</legend>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {digitalKeyLoginClaimOptions.map((claim) => (
                        <label key={claim.claim_key} className="flex items-center gap-2 border border-zinc-100 px-3 py-2 text-sm text-zinc-700">
                          <input type="checkbox" checked={digitalKeyLoginClaims.includes(claim.claim_key)} onChange={() => toggleDigitalKeyLoginClaim(claim.claim_key)} className="h-3.5 w-3.5 border-zinc-300"/>
                          <span>{claim.claim_label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="mt-4 flex justify-end gap-2">
                    <button type="button" onClick={() => { setDigitalKeyLoginFormOpen(false); setDigitalKeyLoginClaims([]); setDigitalKeyLoginNote(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Create request</button>
                  </div>
                </form>
              )}

              {digitalKeyLoginRequests.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="py-2 pr-3 font-medium">Request ID</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium">Assurance</th>
                        <th className="py-2 pr-3 font-medium">Claims</th>
                        <th className="py-2 pr-3 font-medium">Approval context</th>
                        <th className="py-2 pr-3 font-medium">Created</th>
                        <th className="py-2 pr-3 font-medium">Expires</th>
                        <th className="py-2 pr-3 font-medium">Finalized</th>
                        <th className="py-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digitalKeyLoginRequests.map((request) => (
                        <tr key={request.id} className="border-b border-zinc-100 last:border-b-0">
                          <td className="max-w-40 truncate py-3 pr-3 font-mono text-xs text-zinc-600" title={request.id}>{request.id}</td>
                          <td className="py-3 pr-3"><DigitalKeyLoginRequestStatus status={request.status}/></td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{request.requested_claims?.length || 0}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{request.status === 'approved' ? (request.approval_context_summary ? <><p>{approvalSummaryLabel(request.approval_context_summary)}</p><p className="mt-1 text-zinc-500">{request.approval_context_summary.approval_context_version}{request.approval_context_summary.approved_device_label ? ` · ${request.approval_context_summary.approved_device_label}` : ''}</p></> : 'Not available') : '—'}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(request.created_at)}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(request.expires_at)}</td>
                          <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(request.approved_at || request.denied_at || request.expired_at || request.cancelled_at)}</td>
                          <td className="py-3">
                            {request.status === 'pending' && (
                              <div className="flex flex-wrap gap-1.5">
                                <button type="button" onClick={() => runDigitalKeyLoginAction(request, 'approve')} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Approve</button>
                                <button type="button" onClick={() => { setPendingDigitalKeyLoginAction({ action: 'deny', request }); setDigitalKeyLoginReason(''); }} disabled={submitting} className="border border-red-200 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50">Deny</button>
                                <button type="button" onClick={() => runDigitalKeyLoginAction(request, 'expire')} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Expire</button>
                                <button type="button" onClick={() => { setPendingDigitalKeyLoginAction({ action: 'cancel', request }); setDigitalKeyLoginReason(''); }} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500">No internal digital key login requests yet.</p>
              )}

              {pendingDigitalKeyLoginAction && (
                <form onSubmit={submitDigitalKeyLoginAction} className="mt-4 border-t border-zinc-100 pt-4">
                  <p className="text-sm font-medium text-zinc-800">Confirm login request {pendingDigitalKeyLoginAction.action}</p>
                  <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="digital-key-login-request-reason">Reason</label>
                  <textarea id="digital-key-login-request-reason" value={digitalKeyLoginReason} onChange={(event) => setDigitalKeyLoginReason(event.target.value)} required maxLength={500} rows={3}
                    className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" onClick={() => { setPendingDigitalKeyLoginAction(null); setDigitalKeyLoginReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={!digitalKeyLoginReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </section>

      <section className="mb-6 border border-zinc-200 bg-white p-5" aria-labelledby="digital-key-assurance-policy-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="digital-key-assurance-policy-title" className="font-display text-lg font-semibold">Internal Assurance Policy</h2>
            <p className="mt-1 max-w-3xl text-sm text-zinc-600">Internal policy only. This is not an official eIDAS Level of Assurance and does not represent FAS/CSAM recognition.</p>
          </div>
          {digitalKeyAssurancePolicy && <DigitalKeyAssurancePolicyStatus status={digitalKeyAssurancePolicy.status}/>}
        </div>

        {!digitalKeyAssurancePolicy ? (
          <p className="mt-4 text-sm text-zinc-500">Internal assurance policy is not available.</p>
        ) : (
          <>
            <dl className="mt-4 grid gap-x-6 md:grid-cols-3">
              <SummaryRow label="Max internal assurance">{String(digitalKeyAssurancePolicy.max_internal_assurance || 'not_ready').replaceAll('_', ' ')}</SummaryRow>
              <SummaryRow label="Can authenticate">{digitalKeyAssurancePolicy.can_authenticate ? 'Yes' : 'No'}</SummaryRow>
              <SummaryRow label="Evaluated at">{formatDate(digitalKeyAssurancePolicy.evaluated_at)}</SummaryRow>
            </dl>

            <div className="mt-4 grid gap-4 xl:grid-cols-3">
              <div className="border-t border-zinc-100 pt-3">
                <h3 className="text-sm font-medium text-zinc-800">Checks</h3>
                <ul className="mt-2 space-y-1.5 text-xs text-zinc-600">
                  {Object.entries(digitalKeyAssurancePolicy.checks || {}).map(([key, passed]) => <li key={key} className="flex items-start gap-2"><span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${passed ? 'bg-emerald-500' : 'bg-zinc-300'}`} aria-hidden="true"/><span>{key.replaceAll('_', ' ')}</span></li>)}
                </ul>
              </div>
              <div className="border-t border-zinc-100 pt-3">
                <h3 className="text-sm font-medium text-zinc-800">Blockers</h3>
                {digitalKeyAssurancePolicy.blockers?.length ? <ul className="mt-2 space-y-1.5 text-xs text-red-700">{digitalKeyAssurancePolicy.blockers.map((blocker) => <li key={blocker}>{blocker.replaceAll('_', ' ')}</li>)}</ul> : <p className="mt-2 text-xs text-zinc-500">No current blockers.</p>}
                {digitalKeyAssurancePolicy.warnings?.length ? <><h3 className="mt-4 text-sm font-medium text-zinc-800">Warnings</h3><ul className="mt-2 space-y-1.5 text-xs text-amber-800">{digitalKeyAssurancePolicy.warnings.map((warning) => <li key={warning}>{warning.replaceAll('_', ' ')}</li>)}</ul></> : null}
              </div>
              <div className="border-t border-zinc-100 pt-3">
                <h3 className="text-sm font-medium text-zinc-800">Limitations</h3>
                <ul className="mt-2 space-y-1.5 text-xs text-zinc-600">{digitalKeyAssurancePolicy.limitations?.map((limitation) => <li key={limitation}>{limitation}</li>)}</ul>
              </div>
            </div>
          </>
        )}
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,0.6fr)]">
        <section className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Identity summary</h2>
          <dl className="mt-4">
            <SummaryRow label="Name">{identity.display_name}</SummaryRow>
            <SummaryRow label="Masked email">{identity.primary_email}</SummaryRow>
            <SummaryRow label="Masked phone">{identity.phone}</SummaryRow>
            <SummaryRow label="Source verification">
              {identity.source_verification_id ? (
                <Link to={`/dashboard/verifications/${identity.source_verification_id}`} className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-black hover:underline">
                  Open source verification <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5}/>
                </Link>
              ) : null}
            </SummaryRow>
            <SummaryRow label="Verified at">{formatDate(identity.verified_at)}</SummaryRow>
            <SummaryRow label="Created at">{formatDate(identity.created_at)}</SummaryRow>
            <SummaryRow label="Updated at">{formatDate(identity.updated_at)}</SummaryRow>
          </dl>
        </section>

        <aside className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Lifecycle status</h2>
          <p className="mt-1 text-sm text-zinc-600">Internal admin only.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {isActive && (
              <>
                <button type="button" onClick={() => requestLifecycleChange('suspended')} className="border border-amber-300 px-3 py-2 text-sm text-amber-800 hover:border-amber-500">
                  Suspend identity
                </button>
                <button type="button" onClick={() => requestLifecycleChange('revoked')} className="border border-red-200 px-3 py-2 text-sm text-red-700 hover:border-red-400">
                  Revoke identity
                </button>
              </>
            )}
            {isSuspended && (
              <>
                <button type="button" onClick={() => requestLifecycleChange('active')} className="border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500">
                  Reactivate identity
                </button>
                <button type="button" onClick={() => requestLifecycleChange('revoked')} className="border border-red-200 px-3 py-2 text-sm text-red-700 hover:border-red-400">
                  Revoke identity
                </button>
              </>
            )}
          </div>
          {identity.status === 'revoked' && (
            <div className="mt-4 flex items-start gap-2 border border-zinc-200 bg-zinc-50 px-3 py-3 text-sm text-zinc-600">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5}/>
              Revoked identities cannot be reactivated in this version.
            </div>
          )}

          {pendingStatus && (
            <form onSubmit={submitLifecycleChange} className="mt-5 border-t border-zinc-100 pt-4">
              <p className="text-sm font-medium text-zinc-800">Confirm {pendingStatus === 'active' ? 'reactivation' : pendingStatus}</p>
              <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="identity-lifecycle-reason">Reason</label>
              <textarea id="identity-lifecycle-reason" value={reason} onChange={(event) => setReason(event.target.value)} required rows={3}
                className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => { setPendingStatus(null); setReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={!reason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">
                  {submitting ? 'Updating…' : 'Confirm change'}
                </button>
              </div>
            </form>
          )}
        </aside>
      </div>
        </div>
      )}

      {activeTab === 'assurance_claims' && (
        <div id="identity-panel-assurance_claims" role="tabpanel" aria-labelledby="identity-tab-assurance_claims" className="space-y-6">
      {assuranceProfile && (
        <section id="assurance-profile" className="border border-zinc-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Assurance profile</h2>
              <p className="mt-1 max-w-2xl text-sm text-zinc-600">Internal readiness summary based on available idemGO identity evidence. This is not an official FAS, CSAM or eIDAS assurance level or certification.</p>
            </div>
            <AssuranceCandidateLevel level={assuranceProfile.internal_candidate_level}/>
          </div>
          <dl className="mt-4">
            <SummaryRow label="Source">{humanizeAssuranceValue(assuranceProfile.source_type)}</SummaryRow>
            <SummaryRow label="Identity proofing">{humanizeAssuranceValue(assuranceProfile.identity_proofing_strength)}</SummaryRow>
            <SummaryRow label="Digital key readiness">{humanizeAssuranceValue(assuranceProfile.digital_key_readiness)}</SummaryRow>
            <SummaryRow label="Authentication readiness">{humanizeAssuranceValue(assuranceProfile.authentication_readiness)}</SummaryRow>
            <SummaryRow label="Lifecycle">{humanizeAssuranceValue(assuranceProfile.lifecycle_readiness)}</SummaryRow>
            <SummaryRow label="Audit readiness">{humanizeAssuranceValue(assuranceProfile.audit_readiness)}</SummaryRow>
            <SummaryRow label="Internal readiness"><AssuranceCandidateLevel level={assuranceProfile.internal_candidate_level}/></SummaryRow>
          </dl>
          {assuranceProfile.limitations?.length > 0 && (
            <div className="mt-4 border-t border-zinc-100 pt-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Limitations</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {assuranceProfile.limitations.map((limitation) => (
                  <span key={limitation} className="border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">{limitation}</span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {verifiedClaims && (
        <section id="verified-claims" className="border border-zinc-200 bg-white p-5">
          <div>
            <h2 className="font-display text-lg font-semibold">Verified claims</h2>
            <p className="mt-1 max-w-2xl text-sm text-zinc-600">Claim details are derived from safe provenance metadata and do not expose raw identity evidence. Claims are not shared externally yet.</p>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Claim</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Safe value</th>
                  <th className="py-2 pr-4 font-medium">Source</th>
                  <th className="py-2 font-medium">Verified at</th>
                  <th className="py-2 pl-4 font-medium"><span className="sr-only">Claim details</span></th>
                </tr>
              </thead>
              <tbody>
                {(verifiedClaims.claims || []).map((claim) => (
                  <tr key={claim.claim_key} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-3 pr-4">
                      <p className="font-medium text-zinc-800">{claim.claim_label}</p>
                      <p className="mt-0.5 max-w-sm text-xs text-zinc-500">{claim.reason || claim.limitations?.[0] || 'Safe claim provenance is available.'}</p>
                    </td>
                    <td className="py-3 pr-4"><VerifiedClaimStatus status={claim.claim_status}/></td>
                    <td className="py-3 pr-4 text-zinc-600">{claimSafeValue(claim)}</td>
                    <td className="py-3 pr-4 text-zinc-600">{humanizeAssuranceValue(claim.source_type || claim.source)}</td>
                    <td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(claim.computed_at || claim.verified_at)}</td>
                    <td className="py-3 pl-4 text-right"><button type="button" onClick={() => setSelectedClaim(claim)} className="text-xs text-zinc-600 hover:text-black">View details</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {verifiedClaims.limitations?.length > 0 && (
            <div className="mt-4 border-t border-zinc-100 pt-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Limitations</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {verifiedClaims.limitations.map((limitation) => (
                  <span key={limitation} className="border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">{limitation}</span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
        </div>
      )}

      {activeTab === 'consent_sharing' && (
        <div id="identity-panel-consent_sharing" role="tabpanel" aria-labelledby="identity-tab-consent_sharing">
      <section id="consent-foundation" className="border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Consent foundation</h2>
            <p className="mt-1 max-w-2xl text-sm text-zinc-600">Internal consent model for future claim sharing. No claims are shared externally yet.</p>
            <p className="mt-2 text-sm text-zinc-600">The broader audit and governance context is tracked in <Link to="/internal/audit-governance-pack" className="text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">Audit Governance Evidence Pack</Link>.</p>
          </div>
          {isActive && (
            <button type="button" onClick={() => { setConsentDraftOpen((open) => !open); setPendingConsentAction(null); }} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">
              Create internal consent
            </button>
          )}
        </div>

        {consentDraftOpen && (
          <form onSubmit={createDraftConsent} className="mt-4 border-t border-zinc-100 pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <select value={consentRelyingPartyProfileId} onChange={(event) => selectRelyingPartyProfile(event.target.value)} className="border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                <option value="">Free-text relying party</option>
                {relyingPartyProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name} ({profile.rp_ref})</option>)}
              </select>
              <input value={consentRelyingPartyName} onChange={(event) => setConsentRelyingPartyName(event.target.value)} required={!selectedRelyingPartyProfile} disabled={Boolean(selectedRelyingPartyProfile)} maxLength={160}
                placeholder={selectedRelyingPartyProfile ? selectedRelyingPartyProfile.name : 'Relying party name'}
                className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-400"/>
              <input value={consentPurpose} onChange={(event) => setConsentPurpose(event.target.value)} required maxLength={500} placeholder="Purpose"
                className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              <input type="date" value={consentExpiresAt} onChange={(event) => setConsentExpiresAt(event.target.value)}
                className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            </div>
            {selectedRelyingPartyProfile && (
              <p className="mt-3 border border-zinc-100 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                Claims must be both verified and allowed by the selected relying party profile.
                {selectedRelyingPartyProfile.allowed_purposes?.length > 0 && ` Allowed purposes: ${selectedRelyingPartyProfile.allowed_purposes.join(', ')}.`}
              </p>
            )}
            <fieldset className="mt-4">
              <legend className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested claims</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {(verifiedClaims?.claims || []).map((claim) => {
                  const allowedByProfile = !selectedRelyingPartyProfile || (selectedRelyingPartyProfile.allowed_claims || []).includes(claim.claim_key);
                  const selectable = claim.claim_status === 'verified' && allowedByProfile;
                  return (
                    <label key={claim.claim_key} className={`flex items-center gap-2 border px-3 py-2 text-sm ${selectable ? 'cursor-pointer border-zinc-200 text-zinc-700 hover:border-zinc-400' : 'border-zinc-100 text-zinc-400'}`}>
                      <input type="checkbox" checked={consentRequestedClaims.includes(claim.claim_key)} disabled={!selectable}
                        onChange={() => toggleConsentClaim(claim.claim_key)} className="h-3.5 w-3.5 border-zinc-300"/>
                      <span>{claim.claim_label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => { setConsentDraftOpen(false); setConsentRelyingPartyProfileId(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={(!selectedRelyingPartyProfile && !consentRelyingPartyName.trim()) || !consentPurpose.trim() || consentRequestedClaims.length === 0 || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Create internal consent</button>
            </div>
          </form>
        )}

        {consents.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">Consent ref</th>
                  <th className="py-2 pr-3 font-medium">Relying party</th>
                  <th className="py-2 pr-3 font-medium">Purpose</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Requested claims</th>
                  <th className="py-2 pr-3 font-medium">Granted claims</th>
                  <th className="py-2 pr-3 font-medium">Created at</th>
                  <th className="py-2 pr-3 font-medium">Granted at</th>
                  <th className="py-2 pr-3 font-medium">Expires at</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {consents.map((consent) => (
                  <tr key={consent.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{consent.consent_ref}</td>
                    <td className="py-3 pr-3 text-zinc-800">
                      <div>{consent.relying_party_name}</div>
                      {consent.relying_party_profile_ref && <div className="mt-1 font-mono text-[10px] text-zinc-500">{consent.relying_party_profile_ref}</div>}
                    </td>
                    <td className="max-w-48 py-3 pr-3 text-zinc-600">{consent.purpose}</td>
                    <td className="py-3 pr-3"><ConsentStatus status={consent.status}/></td>
                    <td className="max-w-48 py-3 pr-3 text-xs text-zinc-600">{consent.requested_claims?.join(', ') || '—'}</td>
                    <td className="max-w-48 py-3 pr-3 text-xs text-zinc-600">{consent.granted_claims?.join(', ') || '—'}</td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(consent.created_at)}</td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(consent.granted_at)}</td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(consent.expires_at)}</td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {consent.status === 'draft' && <button type="button" onClick={() => grantConsent(consent)} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Grant</button>}
                        {consent.status === 'granted' && <button type="button" onClick={() => requestConsentAction('revoke', consent)} disabled={submitting} className="border border-red-200 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50">Revoke</button>}
                        {['draft', 'granted'].includes(consent.status) && <button type="button" onClick={() => requestConsentAction('expire', consent)} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Expire</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="mt-4 text-sm text-zinc-500">No internal consent records yet.</p>}

        {pendingConsentAction && (
          <form onSubmit={submitConsentAction} className="mt-4 border-t border-zinc-100 pt-4">
            <p className="text-sm font-medium text-zinc-800">Confirm consent {pendingConsentAction.action}</p>
            {pendingConsentAction.action === 'revoke' && (
              <>
                <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="consent-revoke-reason">Reason</label>
                <textarea id="consent-revoke-reason" value={consentReason} onChange={(event) => setConsentReason(event.target.value)} required rows={3}
                  className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              </>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => { setPendingConsentAction(null); setConsentReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={(pendingConsentAction.action === 'revoke' && !consentReason.trim()) || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
            </div>
          </form>
        )}
      </section>

      <section id="claim-sharing-transactions" className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Claim sharing transactions</h2>
            <p className="mt-1 max-w-2xl text-sm text-zinc-600">Internal preview of claims that could be shared based on granted consent. No claims are shared externally.</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-4">
          <select value={claimSharingConsentId} onChange={(event) => setClaimSharingConsentId(event.target.value)} disabled={!isActive || submitting || shareableConsents.length === 0}
            className="min-w-[16rem] border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-400">
            <option value="">Select a granted consent</option>
            {shareableConsents.map((consent) => <option key={consent.id} value={consent.id}>{consent.relying_party_name} — {consent.purpose}</option>)}
          </select>
          <button type="button" onClick={prepareClaimSharingTransaction} disabled={!isActive || !claimSharingConsentId || submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:cursor-not-allowed disabled:opacity-50">Prepare from consent</button>
          {!shareableConsents.length && <p className="text-xs text-zinc-500">A granted, active consent is required.</p>}
        </div>

        {claimSharingTransactions.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">Share ref</th>
                  <th className="py-2 pr-3 font-medium">Relying party</th>
                  <th className="py-2 pr-3 font-medium">Purpose</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Consent ref</th>
                  <th className="py-2 pr-3 font-medium">Prepared claims</th>
                  <th className="py-2 pr-3 font-medium">Skipped claims</th>
                  <th className="py-2 pr-3 font-medium">Created at</th>
                  <th className="py-2 pr-3 font-medium">Completed at</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {claimSharingTransactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{transaction.share_ref}</td>
                    <td className="py-3 pr-3 text-zinc-800">{transaction.relying_party_name}</td>
                    <td className="max-w-48 py-3 pr-3 text-zinc-600">{transaction.purpose}</td>
                    <td className="py-3 pr-3"><ClaimSharingStatus status={transaction.status}/>{transaction.blocked_reason && <div className="mt-1 text-[10px] text-red-600">{transaction.blocked_reason.replaceAll('_', ' ')}</div>}</td>
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{transaction.consent_ref || '—'}</td>
                    <td className="max-w-48 py-3 pr-3 text-xs text-zinc-600">{transaction.prepared_claims?.length ? transaction.prepared_claims.map((claim) => `${claim.claim_key}: ${String(claim.safe_value)}`).join(', ') : '—'}</td>
                    <td className="max-w-48 py-3 pr-3 text-xs text-zinc-600">{transaction.skipped_claims?.length ? transaction.skipped_claims.map((claim) => `${claim.claim_key} (${claim.reason.replaceAll('_', ' ')})`).join(', ') : '—'}</td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(transaction.created_at)}</td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(transaction.completed_at)}</td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {transaction.status === 'prepared' && <button type="button" onClick={() => completeClaimSharingTransaction(transaction)} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Complete</button>}
                        {transaction.status === 'prepared' && <button type="button" onClick={() => { setPendingClaimSharingCancellation(transaction); setClaimSharingCancellationReason(''); }} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Cancel</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="mt-4 text-sm text-zinc-500">No internal claim sharing transactions yet.</p>}

        {pendingClaimSharingCancellation && (
          <form onSubmit={submitClaimSharingCancellation} className="mt-4 border-t border-zinc-100 pt-4">
            <p className="text-sm font-medium text-zinc-800">Cancel {pendingClaimSharingCancellation.share_ref}</p>
            <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="claim-sharing-cancel-reason">Reason (optional)</label>
            <textarea id="claim-sharing-cancel-reason" value={claimSharingCancellationReason} onChange={(event) => setClaimSharingCancellationReason(event.target.value)} maxLength={500} rows={3}
              className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => { setPendingClaimSharingCancellation(null); setClaimSharingCancellationReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Back</button>
              <button type="submit" disabled={submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Cancel transaction</button>
            </div>
          </form>
        )}
      </section>

        </div>
      )}

      {activeTab === 'devices_sessions' && (
        <div id="identity-panel-devices_sessions" role="tabpanel" aria-labelledby="identity-tab-devices_sessions">
      <div className="grid gap-6 xl:grid-cols-2">
        <section id="trusted-devices" className="border border-zinc-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Trusted devices</h2>
              <p className="mt-1 text-sm text-zinc-600">Internal admin only.</p>
            </div>
            <button type="button" onClick={() => { setTestDeviceOpen((open) => !open); setPendingAction(null); }} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">
              <Plus className="h-4 w-4" strokeWidth={1.5}/> Add test device
            </button>
          </div>
          {testDeviceOpen && (
            <form onSubmit={createTestDevice} className="mt-4 grid gap-3 border-t border-zinc-100 pt-4 sm:grid-cols-[minmax(0,1fr)_8rem_auto]">
              <input value={deviceLabel} onChange={(event) => setDeviceLabel(event.target.value)} placeholder="Optional label" maxLength={120}
                className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              <select value={deviceType} onChange={(event) => setDeviceType(event.target.value)} className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                <option value="unknown">Unknown</option>
                <option value="mobile">Mobile</option>
                <option value="desktop">Desktop</option>
              </select>
              <button type="submit" disabled={submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Add</button>
            </form>
          )}
          {devices.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr><th className="py-2 pr-3 font-medium">Device ref</th><th className="py-2 pr-3 font-medium">Label</th><th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Last seen</th><th className="py-2 pr-3 font-medium">Created at</th><th className="py-2 font-medium">Actions</th></tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id} className="border-b border-zinc-100 last:border-b-0">
                      <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{device.device_ref}</td>
                      <td className="py-3 pr-3 text-zinc-800">{device.label || '—'}</td>
                      <td className="py-3 pr-3 text-zinc-600">{device.device_type}</td>
                      <td className="py-3 pr-3"><DeviceStatus status={device.status}/></td>
                      <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(device.last_seen_at)}</td>
                      <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(device.created_at)}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {device.status === 'trusted' && <button type="button" onClick={() => requestAction('device', device, 'blocked')} className="border border-amber-300 px-2 py-1 text-xs text-amber-800 hover:border-amber-500">Block</button>}
                          {['trusted', 'blocked'].includes(device.status) && <button type="button" onClick={() => requestAction('device', device, 'removed')} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400">Remove</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="mt-4 text-sm text-zinc-500">No trusted devices yet.</p>}
          {pendingAction?.kind === 'device' && (
            <form onSubmit={submitPendingAction} className="mt-4 border-t border-zinc-100 pt-4">
              <p className="text-sm font-medium text-zinc-800">Confirm device {pendingAction.status}</p>
              <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="device-action-reason">Reason</label>
              <textarea id="device-action-reason" value={reason} onChange={(event) => setReason(event.target.value)} required rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => setPendingAction(null)} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">Cancel</button><button type="submit" disabled={!reason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white disabled:opacity-50">Confirm</button></div>
            </form>
          )}
        </section>

        <section id="identity-sessions" className="border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">Identity sessions</h2>
          <p className="mt-1 text-sm text-zinc-600">Internal admin only.</p>
          {sessions.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr><th className="py-2 pr-3 font-medium">Session ref</th><th className="py-2 pr-3 font-medium">Device</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">IP address</th><th className="py-2 pr-3 font-medium">User agent</th><th className="py-2 pr-3 font-medium">Last seen</th><th className="py-2 pr-3 font-medium">Expires at</th><th className="py-2 pr-3 font-medium">Created at</th><th className="py-2 font-medium">Actions</th></tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id} className="border-b border-zinc-100 last:border-b-0">
                      <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{session.session_ref}</td>
                      <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{session.device_ref || '—'}</td>
                      <td className="py-3 pr-3"><SessionStatus status={session.status}/></td>
                      <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{session.ip_address || '—'}</td>
                      <td className="max-w-44 py-3 pr-3 text-xs text-zinc-500" title={session.user_agent || undefined}>{truncateUserAgent(session.user_agent)}</td>
                      <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(session.last_seen_at)}</td>
                      <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(session.expires_at)}</td>
                      <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(session.created_at)}</td>
                      <td className="py-3">{session.status === 'active' && <button type="button" onClick={() => requestAction('session', session)} className="border border-red-200 px-2 py-1 text-xs text-red-700 hover:border-red-400">Revoke</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="mt-4 text-sm text-zinc-500">No identity sessions yet.</p>}
          {pendingAction?.kind === 'session' && (
            <form onSubmit={submitPendingAction} className="mt-4 border-t border-zinc-100 pt-4">
              <p className="text-sm font-medium text-zinc-800">Confirm session revocation</p>
              <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="session-revoke-reason">Reason</label>
              <textarea id="session-revoke-reason" value={reason} onChange={(event) => setReason(event.target.value)} required rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
              <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => setPendingAction(null)} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">Cancel</button><button type="submit" disabled={!reason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white disabled:opacity-50">Revoke session</button></div>
            </form>
          )}
        </section>
      </div>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Authentication sandbox</h2>
            <p className="mt-1 text-sm text-zinc-600">Internal test flow for future idem authentication.</p>
          </div>
          {trustedDevices.length > 0 && (
            <button type="button" onClick={() => { setAuthenticationStartOpen((open) => !open); setPendingAuthenticationAction(null); }} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">
              Start test authentication
            </button>
          )}
        </div>

        {trustedDevices.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500">Add a trusted test device before starting authentication.</p>
        ) : authenticationStartOpen && (
          <form onSubmit={startTestAuthentication} className="mt-4 grid gap-3 border-t border-zinc-100 pt-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
            <select value={authenticationDeviceId} onChange={(event) => setAuthenticationDeviceId(event.target.value)} required className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
              <option value="">Select trusted device</option>
              {trustedDevices.map((device) => <option key={device.id} value={device.id}>{device.label || device.device_ref} ({device.device_type})</option>)}
            </select>
            <input value={authenticationPurpose} onChange={(event) => setAuthenticationPurpose(event.target.value)} maxLength={120} placeholder="Purpose" className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <button type="submit" disabled={!authenticationDeviceId || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Start</button>
          </form>
        )}

        {authenticationRequests.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr><th className="py-2 pr-3 font-medium">Auth ref</th><th className="py-2 pr-3 font-medium">Device ref</th><th className="py-2 pr-3 font-medium">Purpose</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Created at</th><th className="py-2 pr-3 font-medium">Expires at</th><th className="py-2 pr-3 font-medium">Result session</th><th className="py-2 font-medium">Actions</th></tr>
              </thead>
              <tbody>
                {authenticationRequests.map((request) => (
                  <tr key={request.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{request.auth_ref}</td>
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{request.device_ref || '—'}</td>
                    <td className="py-3 pr-3 text-zinc-600">{request.purpose}</td>
                    <td className="py-3 pr-3"><AuthenticationRequestStatus status={request.status}/></td>
                    <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(request.created_at)}</td>
                    <td className="py-3 pr-3 text-xs text-zinc-500">{formatDate(request.expires_at)}</td>
                    <td className="py-3 pr-3 font-mono text-xs text-zinc-600">{request.result_session_ref || '—'}</td>
                    <td className="py-3">
                      {request.status === 'pending' && (
                        <div className="flex flex-wrap gap-1.5">
                          <button type="button" onClick={() => approveTestAuthentication(request)} disabled={submitting} className="border border-emerald-300 px-2 py-1 text-xs text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Approve</button>
                          <button type="button" onClick={() => requestAuthenticationAction('deny', request)} disabled={submitting} className="border border-red-200 px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-50">Deny</button>
                          <button type="button" onClick={() => requestAuthenticationAction('expire', request)} disabled={submitting} className="border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Expire</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : trustedDevices.length > 0 ? <p className="mt-4 text-sm text-zinc-500">No authentication requests yet.</p> : null}

        {pendingAuthenticationAction && (
          <form onSubmit={submitAuthenticationAction} className="mt-4 border-t border-zinc-100 pt-4">
            <p className="text-sm font-medium text-zinc-800">Confirm test authentication {pendingAuthenticationAction.action}</p>
            <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="authentication-action-reason">
              Reason {pendingAuthenticationAction.action === 'deny' ? '' : '(optional)'}
            </label>
            <textarea id="authentication-action-reason" value={reason} onChange={(event) => setReason(event.target.value)} required={pendingAuthenticationAction.action === 'deny'} rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => setPendingAuthenticationAction(null)} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400">Cancel</button><button type="submit" disabled={(pendingAuthenticationAction.action === 'deny' && !reason.trim()) || submitting} className="bg-black px-3 py-2 text-sm text-white disabled:opacity-50">Confirm</button></div>
          </form>
        )}
      </section>

        </div>
      )}

      {activeTab === 'audit' && (
      <div id="identity-panel-audit" role="tabpanel" aria-labelledby="identity-tab-audit">
      <section id="event-timeline" className="border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Event timeline</h2>
            <p className="mt-1 text-sm text-zinc-600">{auditLoading ? 'Loading events…' : `Showing ${auditEvents.length} of ${auditPagination.total} sanitized internal events.`}</p>
            <p className="mt-1 text-xs text-zinc-500">Audit details are sanitized and do not include raw identity evidence.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => exportAuditEvents('json')} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400"><Download className="h-3.5 w-3.5" strokeWidth={1.5}/> Export JSON</button>
            <button type="button" onClick={() => exportAuditEvents('csv')} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400"><Download className="h-3.5 w-3.5" strokeWidth={1.5}/> Export CSV</button>
          </div>
        </div>
        <div className="mt-4 grid gap-2 border-t border-zinc-100 pt-4 sm:grid-cols-4">
          <select value={auditFilters.type} onChange={(event) => setAuditFilters((current) => ({ ...current, type: event.target.value }))} className="border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
            <option value="">All event types</option>
            {Object.entries(actionLabels).map(([type, label]) => <option key={type} value={type}>{label}</option>)}
          </select>
          <input type="date" value={auditFilters.from} onChange={(event) => setAuditFilters((current) => ({ ...current, from: event.target.value }))} aria-label="Events from date" className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
          <input type="date" value={auditFilters.to} onChange={(event) => setAuditFilters((current) => ({ ...current, to: event.target.value }))} aria-label="Events to date" className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
          <div className="flex gap-2"><button type="button" onClick={() => loadAuditEvents()} disabled={auditLoading} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Apply</button><button type="button" onClick={() => { const filters = { type: '', from: '', to: '' }; setAuditFilters(filters); loadAuditEvents(filters); }} disabled={auditLoading} className="px-2 py-2 text-sm text-zinc-600 hover:text-black disabled:opacity-50">Clear</button></div>
        </div>
        {auditEvents.length ? (
          <ol className="mt-4 divide-y divide-zinc-100">
            {auditEvents.map((event) => (
              <li key={event.id || `${event.action}-${event.created_at}`} className="flex flex-wrap items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div>
                  <p className="text-sm font-medium text-zinc-800">{eventLabel(event.event_type || event.action)}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">{event.actor_email || 'System'}</p>
                  <EventMetadata metadata={event.metadata}/>
                </div>
                <div className="flex shrink-0 items-center gap-3"><time className="text-xs text-zinc-500">{formatDate(event.created_at)}</time><button type="button" onClick={() => setSelectedAuditEvent(event)} className="text-xs text-zinc-600 hover:text-black">Details</button></div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="mt-4 text-sm text-zinc-500">No identity events match these filters.</p>
        )}
      </section>
      </div>
      )}

      {selectedClaim && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4" role="dialog" aria-modal="true" aria-labelledby="claim-provenance-title">
          <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto border border-zinc-200 bg-white p-5 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="claim-provenance-title" className="font-display text-lg font-semibold">{selectedClaim.claim_label}</h2>
                <p className="mt-1 text-sm text-zinc-600">Safe internal claim provenance.</p>
              </div>
              <button type="button" onClick={() => setSelectedClaim(null)} aria-label="Close claim details" className="grid h-8 w-8 place-items-center border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-black"><X className="h-4 w-4" strokeWidth={1.5}/></button>
            </div>
            <dl className="mt-4 border-t border-zinc-100 pt-1">
              <SummaryRow label="Claim key">{selectedClaim.claim_key}</SummaryRow>
              <SummaryRow label="Status"><VerifiedClaimStatus status={selectedClaim.claim_status}/></SummaryRow>
              <SummaryRow label="Safe value">{claimSafeValue(selectedClaim)}</SummaryRow>
              <SummaryRow label="Source">{humanizeAssuranceValue(selectedClaim.source_type || selectedClaim.source)}</SummaryRow>
              <SummaryRow label="Source verification">{selectedClaim.source_verification_id || 'Not applicable'}</SummaryRow>
              <SummaryRow label="Source method">{humanizeAssuranceValue(selectedClaim.source_verification_method)}</SummaryRow>
              <SummaryRow label="Computed at">{formatDate(selectedClaim.computed_at || selectedClaim.verified_at)}</SummaryRow>
              <SummaryRow label="Reason">{selectedClaim.reason || selectedClaim.limitations?.[0] || 'No additional provenance reason is available.'}</SummaryRow>
              <SummaryRow label="Limitations">{selectedClaim.limitations?.length ? selectedClaim.limitations.join('; ') : 'None recorded'}</SummaryRow>
              <SummaryRow label="Privacy note">{selectedClaim.privacy_note || 'Claim details contain safe metadata only.'}</SummaryRow>
            </dl>
            <p className="mt-4 text-xs text-zinc-500">This view does not expose raw date of birth, document data, images, biometrics, device fingerprints, tokens, or other raw source evidence.</p>
          </div>
        </div>
      )}

      {selectedAuditEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4" role="dialog" aria-modal="true" aria-label="Identity audit event details">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-zinc-200 bg-white p-5 shadow-lg">
            <div className="flex items-start justify-between gap-3"><div><h2 className="font-display text-lg font-semibold">{eventLabel(selectedAuditEvent.event_type || selectedAuditEvent.action)}</h2><p className="mt-1 text-sm text-zinc-600">{formatDate(selectedAuditEvent.created_at)} · {selectedAuditEvent.actor_email || selectedAuditEvent.actor_user_id || 'System'}</p></div><button type="button" onClick={() => setSelectedAuditEvent(null)} aria-label="Close audit event details" className="grid h-8 w-8 place-items-center border border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-black"><X className="h-4 w-4" strokeWidth={1.5}/></button></div>
            <div className="mt-4 border-t border-zinc-100 pt-4"><p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Safe metadata</p><pre className="mt-2 overflow-x-auto bg-zinc-50 p-3 text-xs text-zinc-700">{JSON.stringify(selectedAuditEvent.metadata || {}, null, 2)}</pre></div>
            <p className="mt-4 text-xs text-zinc-500">Audit details are sanitized and do not include raw identity evidence.</p>
          </div>
        </div>
      )}
    </div>
  );
}
